import { ScanLabelQuota } from './scan-label-interface';

// ── Client → Server params ──────────────────────────────────────────

export interface ScanMealPhotoParams {
  imageBase64: string;       // base64-encoded image (JPEG), max 700KB
  model?: string;            // a model key from SCAN_MODELS
  // The user's active UI language (#4206). This scanner's AI output is not
  // localized today: the identified food `name` is deliberately a generic
  // ENGLISH nutrition-database search key, and nothing else here is prose the
  // app authors. The field is carried so a future framing hook can read
  // `params.locale` without needing another client release.
  locale?: string;
  // Asks the server to also pick a database food for each food it names (#5623). The app sets it
  // only while the MealScanAiPick config key is on. A server that does not know the field ignores
  // it, and a scan without it costs no second AI call.
  pickFoods?: boolean;
}

// ── Server → Client results ─────────────────────────────────────────

/** What the server's second AI call decided for one food (#5623). */
export enum MealScanPickOutcome {
  /** the model chose one of the candidates; `foodId` names it */
  Picked = 'picked',
  /** the model looked at the candidates and rejected all of them: the user picks by hand */
  None = 'none',
}

export interface MealScanFoodPick {
  outcome: MealScanPickOutcome;
  /** the database food the model chose; set only when `outcome` is `Picked` */
  foodId?: string;
}

export interface DetectedFoodItem {
  name: string;              // English name optimized for nutrition database search
  estimatedGrams: number;    // estimated weight in grams
  confidence: number;        // 0-1 confidence in identification
  // The server's pick for this food (#5623). ABSENT whenever the server did not decide: the scan
  // did not ask for picks, the food had no candidates, or the pick step failed or ran out of time.
  // The app then picks with its own rule, as it always did. See design-docs/meal-photo-scanner.md.
  pick?: MealScanFoodPick;
}

export interface ScanMealPhotoResults {
  foods?: DetectedFoodItem[];
  noFoodsReason?: 'blurry' | 'no_food' | 'nutrition_label' | 'barcode';  // why no foods were detected ('nutrition_label' / 'barcode' = photo of packaging, not a plated meal — the client redirects to the matching scanner, #3902)
  modelKey?: string;
  error?: string;
  quota?: ScanLabelQuota;
}
